import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Check, Search } from 'lucide-react';
import { ScheduledShiftView } from '../../../../types/scheduling';
import {
  parseLocalIso,
  bucketForShiftDate,
  formatLocalShiftDate,
  formatLocalShiftTimeRange,
  ShiftBucket,
} from './format';

interface ShiftPickerProps {
  shifts: ScheduledShiftView[];
  value: string;
  onChange: (id: string) => void;
  loading?: boolean;
  disabled?: boolean;
  locale?: string;
  emptyLabel?: string;
  testId?: string;
}

const BUCKET_ORDER: ShiftBucket[] = ['today', 'tomorrow', 'thisWeek', 'nextWeek', 'later'];

interface Row {
  // Selection key. Virtual (rule-generated, not-yet-materialized) occurrences all come back with
  // id === 0, so keying by id collapses every upcoming shift to one option. The shift's UTC
  // startTime is stable and unique per user (nobody works two shifts at the same instant), and it
  // is exactly what the swap request payload carries — so it is the correct key.
  val: string;
  dateLabel: string;
  rangeLabel: string;
  search: string;
}

// A grouped, scannable shift selector. Replaces the flat "Mon, 08:00 - 17:00" dropdown, which
// collapses many identical-time shifts into indistinguishable rows. Shifts are grouped into
// Today / Tomorrow / This week / Next week / Later so a specific date is findable at a glance.
const ShiftPicker: React.FC<ShiftPickerProps> = ({
  shifts,
  value,
  onChange,
  loading,
  disabled,
  locale,
  emptyLabel,
  testId,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const bucketLabel: Record<ShiftBucket, string> = {
    today: t('Today'),
    tomorrow: t('Tomorrow'),
    thisWeek: t('This week'),
    nextWeek: t('Next week'),
    later: t('Later'),
  };

  const grouped = useMemo(() => {
    const map = new Map<ShiftBucket, Row[]>();
    for (const s of shifts) {
      const startLocal = String(s.localStartTime ?? s.startTime);
      // Bucket by the company-local wall-clock date (never the browser-shifted UTC instant).
      const startDate = parseLocalIso(startLocal) ?? new Date(s.startTime);
      const bucket = bucketForShiftDate(startDate);
      const dateLabel = formatLocalShiftDate(startLocal, locale);
      const rangeLabel = formatLocalShiftTimeRange(
        String(s.localStartTime ?? s.startTime),
        String(s.localEndTime ?? s.endTime),
        locale,
      );
      const arr = map.get(bucket) ?? [];
      arr.push({ val: String(s.startTime), dateLabel, rangeLabel, search: `${dateLabel} ${rangeLabel}`.toLowerCase() });
      map.set(bucket, arr);
    }
    return map;
  }, [shifts, locale]);

  if (loading) {
    return (
      <div className="space-y-2" data-testid={testId}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg border-2 border-border bg-muted/40" />
        ))}
      </div>
    );
  }

  if (shifts.length === 0) {
    return (
      <div
        className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground"
        data-testid={testId}
      >
        {emptyLabel ?? t('No shifts to choose from')}
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const showSearch = shifts.length > 6;
  const noMatches = q !== '' && BUCKET_ORDER.every((b) => (grouped.get(b) ?? []).every((r) => !r.search.includes(q)));

  // One flat list of the currently-visible rows, in the exact order they render (BUCKET_ORDER,
  // then the q-filter). Arrow-key navigation walks this so focus crosses bucket boundaries.
  const visibleRows: Row[] = BUCKET_ORDER.flatMap((bucket) =>
    (grouped.get(bucket) ?? []).filter((r) => q === '' || r.search.includes(q)),
  );
  const rowIndex = new Map(visibleRows.map((r, i) => [r.val, i] as const));
  const selectedIndex = visibleRows.findIndex((r) => r.val === value);

  return (
    <div data-testid={testId}>
      {showSearch && (
        <div className="relative mb-2">
          <Search size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Search shifts')}
            aria-label={t('Search shifts')}
            disabled={disabled}
            className="h-9 w-full rounded-lg border border-border bg-background ps-9 pe-3 text-sm text-foreground placeholder-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
          />
        </div>
      )}

      <div className="max-h-64 space-y-3 overflow-y-auto pe-1" role="radiogroup" aria-label={t('Select a shift')}>
        {BUCKET_ORDER.map((bucket) => {
          const rows = (grouped.get(bucket) ?? []).filter((r) => q === '' || r.search.includes(q));
          if (rows.length === 0) return null;
          return (
            <div key={bucket}>
              <p className="mb-1.5 px-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {bucketLabel[bucket]}
              </p>
              <div className="space-y-1.5">
                {rows.map((r) => {
                  const selected = r.val === value;
                  const i = rowIndex.get(r.val) ?? 0;
                  return (
                    <button
                      key={r.val}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={disabled}
                      tabIndex={i === (selectedIndex >= 0 ? selectedIndex : 0) ? 0 : -1}
                      ref={(el) => { optionRefs.current[i] = el; }}
                      onClick={() => onChange(r.val)}
                      onKeyDown={(e) => {
                        const move = (to: number) => {
                          const n = visibleRows.length;
                          if (n === 0) return;
                          const next = ((to % n) + n) % n;
                          onChange(visibleRows[next].val);
                          optionRefs.current[next]?.focus();
                        };
                        switch (e.key) {
                          case 'ArrowDown':
                          case 'ArrowRight':
                            e.preventDefault();
                            move(i + 1);
                            break;
                          case 'ArrowUp':
                          case 'ArrowLeft':
                            e.preventDefault();
                            move(i - 1);
                            break;
                          case 'Home':
                            e.preventDefault();
                            move(0);
                            break;
                          case 'End':
                            e.preventDefault();
                            move(visibleRows.length - 1);
                            break;
                          default:
                            break;
                        }
                      }}
                      data-testid={`shift-option-${r.val}`}
                      className={`flex w-full items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-start transition-colors disabled:opacity-50 ${
                        selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <CalendarClock size={16} className={`shrink-0 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-sm font-medium ${selected ? 'text-primary' : 'text-foreground'}`}>
                          {r.dateLabel}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground tabular-nums">{r.rangeLabel}</span>
                      </span>
                      {selected && <Check size={16} className="shrink-0 text-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {noMatches && <p className="px-1 py-2 text-xs text-muted-foreground">{t('No shifts match your search')}</p>}
      </div>
    </div>
  );
};

export default ShiftPicker;
